import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Button, Dialog } from 'components/ui';
import { ApiClient } from 'lib/api/client';
import { ListingPriceGridBroadcastRequest } from 'lib/api/generated';
import { getFormFieldErrorsFromResponse } from 'lib/helpers';
import { useStore } from 'lib/store';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { SendBroadcastInputStep } from './send-broadcast-input-step';
import { SendBroadcastSuccessStep } from './send-broadcast-success-step';

enum Step {
  input = 'Send a broadcast',
  success = 'Broadcast sent',
}

export interface SendBroadcastDialogForm {
  message: string;
  link?: string;
}

export interface SendBroadcastDialogProps {
  listingId: string;
  isOpen?: boolean;
  onDismiss?: VoidFunction;
}

export const SendBroadcastDialog: FC<SendBroadcastDialogProps> = ({
  listingId,
  isOpen,
  onDismiss = () => undefined,
}) => {
  const [currentStep, setCurrentStep] = useState(Step.input);
  const methods = useForm<SendBroadcastDialogForm>({ defaultValues: {} });
  const { handleSubmit, watch, setError, reset } = methods;
  const buyerOrgId = useStore((store) => store.activeBuyerOrgId);
  const message = watch('message');

  const sendBroadcastMutation = useMutation(
    (listingPriceGridBroadcastRequest: ListingPriceGridBroadcastRequest) =>
      new ApiClient().listingApi.orgMarketplaceListingBroadcastCreate({
        listingId,
        buyerOrgId: buyerOrgId || '',
        listingPriceGridBroadcastRequest,
      })
  );

  const onSubmit = async (data: SendBroadcastDialogForm) => {
    try {
      await sendBroadcastMutation.mutateAsync(data);
      setCurrentStep(Step.success);
    } catch (error) {
      if (!axios.isAxiosError(error)) return;

      if (error?.response?.data?.errors) {
        const errors = getFormFieldErrorsFromResponse(
          error.response?.data.errors
        );
        errors.forEach(({ field, message }) => setError(field, { message }));
      } else {
        // Fallback error
        toast.error('Unable to send broadcast, please try again later');
      }
    }
  };

  const actions = useMemo(() => {
    if (currentStep === Step.input) {
      return (
        <Button
          form='sendBroadcastForm'
          type='submit'
          size='small'
          disabled={!message}
          isLoading={sendBroadcastMutation.isLoading}
        >
          Send broadcast
        </Button>
      );
    }

    if (currentStep === Step.success) {
      return (
        <Button size='small' onClick={onDismiss}>
          Back to Listing Details
        </Button>
      );
    }
  }, [currentStep, message, onDismiss, sendBroadcastMutation.isLoading]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(Step.input);
      reset();
    }
  }, [isOpen, reset]);

  return (
    <FormProvider {...methods}>
      <Dialog
        title={currentStep}
        isOpen={isOpen}
        actions={actions}
        onDismiss={onDismiss}
      >
        <form id='sendBroadcastForm' onSubmit={handleSubmit(onSubmit)}>
          {currentStep === Step.input && <SendBroadcastInputStep />}
          {currentStep === Step.success && <SendBroadcastSuccessStep />}
        </form>
      </Dialog>
    </FormProvider>
  );
};
